import React from 'react'
import { supabaseClient } from '@/configs/supabase'
import { useSubscription } from '@/lib/supabase/hooks/useSubscription'
import { useUserAuthData } from '@/state/userAuth/hooks'
import {
  getUserKnowledgeTest,
  getUserKyc,
  getUserSuitabilityTest,
  getUserVerificationDetailsWithRelatedData
} from '@/lib/supabase/userVerificationDetails'
import {
  ISubscriptionHookResponse,
  TUserKnowledgeTestSelectKey,
  TUserKycSelectKey,
  TUserSuitabilityTestSelectKey,
  TUserVerificationDetailsSelectKey
} from '@/lib/supabase/supabase.interface'
import _ from 'lodash'

export const useUserVerificationDetails =
  (): ISubscriptionHookResponse<TUserVerificationDetailsSelectKey> => {
    const userAuthData = useUserAuthData()

    const { rows, status } = useSubscription(
      'UserVerificationDetails',
      {
        k: 'user_id',
        v: userAuthData?.id ?? ''
      },
      (): any =>
        getUserVerificationDetailsWithRelatedData(
          supabaseClient,
          userAuthData?.id as string
        )
    )
    return { data: rows?.[0], subLifeCycleStatus: status }
  }

export const useUserSuitabilityTest = (
  suitTestId: string | null | undefined
): ISubscriptionHookResponse<TUserSuitabilityTestSelectKey> => {
  const shouldFetch = !_.isNil(suitTestId)

  const { rows, status } = useSubscription(
    'UserSuitabilityTest',
    shouldFetch ? { k: 'id', v: suitTestId } : undefined,
    shouldFetch
      ? (): any => getUserSuitabilityTest(supabaseClient, suitTestId)
      : undefined
  )

  // If suitTestId is null, override the default values
  const data = shouldFetch ? rows?.[0] : undefined
  const subLifeCycleStatus = shouldFetch ? status : 'unknown'

  return { data, subLifeCycleStatus }
}

export const useUserKnowledgeTest = (
  knowledgeTestId: string | null | undefined
): ISubscriptionHookResponse<TUserKnowledgeTestSelectKey> => {
  const shouldFetch = !_.isNil(knowledgeTestId)

  const { rows, status } = useSubscription(
    'UserKnowledgeTest',
    shouldFetch ? { k: 'id', v: knowledgeTestId } : undefined,
    shouldFetch
      ? (): any => getUserKnowledgeTest(supabaseClient, knowledgeTestId)
      : undefined
  )

  // If knowledgeTestId is null, override the default values
  const data = shouldFetch ? rows?.[0] : undefined
  const subLifeCycleStatus = shouldFetch ? status : 'unknown'

  return { data, subLifeCycleStatus }
}

export const useUserKyc = (
  kycId: string | null | undefined
): ISubscriptionHookResponse<TUserKycSelectKey> => {
  const shouldFetch = !_.isNil(kycId)

  const { rows, status } = useSubscription(
    'UserKyc',
    shouldFetch ? { k: 'id', v: kycId } : undefined,
    shouldFetch ? (): any => getUserKyc(supabaseClient, kycId) : undefined
  )

  // If kycId is null, override the default values
  const data = shouldFetch ? rows?.[0] : undefined
  const subLifeCycleStatus = shouldFetch ? status : 'unknown'

  return { data, subLifeCycleStatus }
}
